import Config from './Config.ts';

/**
 * Resolves a reCAPTCHA v3 token for `action`, or `undefined` when there is no
 * reCAPTCHA to ask.
 *
 * Every call site used to spell this as `executeRecaptcha ? await
 * executeRecaptcha(action) : undefined`, which LOOKS like it handles an
 * unconfigured reCAPTCHA but never does: `GoogleReCaptchaProvider` hands back a
 * function even when `reCaptchaKey` is `''`, and `Config` bakes `recaptcha` as
 * undefined whenever `VITE_RECAPTCHA_SITE_KEY` is absent. The function then
 * rejects, so the ternary's false branch is unreachable and the whole OTP flow
 * is impossible to run locally or in the widget-demo harness.
 *
 * The skip is gated on `environment === 'local'` — the same discriminator
 * `index.tsx` and `DownloadDorsia` already use — and NOT on "no site key
 * configured". That distinction is deliberate: today a missing key in a real
 * build breaks the OTP loudly, whereas keying off absence would send OTPs with
 * no bot protection at all, since the token is an optional argument every step
 * of the way down and nothing would reject a request lacking it. A build
 * misconfiguration must not quietly downgrade into a weaker security posture.
 *
 * Callers keep passing their own `executeRecaptcha` because it comes from a hook
 * and cannot be read here.
 */
export const recaptchaTokenFor = async (
  action: string,
  executeRecaptcha: ((action: string) => Promise<string>) | undefined
): Promise<string | undefined> => {
  if (Config.data.environment === 'local') return undefined;
  if (!executeRecaptcha) return undefined;

  return executeRecaptcha(action);
};
